import React, { useEffect, useState, useRef } from 'react';
import {  useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useReactToPrint } from 'react-to-print';
import moment from 'moment';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import Button from 'components/Button2';
import ButtonLink from 'components/ButtonLink';

import './index.css';

const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];

function AssignmentReport() {
    const [assignmentData, setAssignmentData] = useState(null);

    const { user } = useAuthContext();
    const { assignmentId } = useParams();
    const printRef = useRef(null);

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current
    });

    useEffect(() => {
        axios.get(`tutors/assignments/report/${assignmentId}/`)
            .then(res => {
                // console.log(res.data);
                setAssignmentData(res.data);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    if (!assignmentData) return <></>;

    return (
        <div className='assignmentReport' ref={printRef}>
            <Helmet>
                <title>
                    Test Report - Test Center Admin
                </title>
            </Helmet>
            <div className='assignmentReport__headerGroup'>
                <div className='assignmentReport__headerSection'>
                    {assignmentData.logo && 
                        <img
                            className='assignmentReport__headerLogo'
                            alt='logo'
                            src={assignmentData.logo}
                        />
                    }
                </div>
                <div className='assignmentReport__headerSection'>
                    <div className='assignmentReport__headerInfo'>
                        <p>
                            Name: {assignmentData.studentName}
                        </p>
                        <p>
                            Email: {assignmentData.email}
                        </p>
                        <p>
                            Assignment name: {assignmentData.name}
                        </p>
                        <p>
                            Completed on: {moment(assignmentData.completedDate).format('MMMM D YYYY')}
                        </p>
                    </div>
                </div>
            </div>
            <div className='assignmentReport__headerGroup'>
                <h1 className='assignmentReport__headerTitle'>
                    Assignment Result Overview
                </h1>
                <div className='assignmentReport__buttonGroup'>
                    <ButtonLink className='assignmentReport__button--blue' to={`/review/assignment/${assignmentId}`}>
                        REVIEW QUESTIONS
                    </ButtonLink>
                    <Button className='assignmentReport__button' secondary onClick={handlePrint}>
                        PRINT REPORT
                    </Button>
                </div>
            </div>
            <div className='assignmentReport__questions'>
                <div className='assignmentReport__questionsOverview'>
                    <div className='assignmentReport__questionsSummary'>
                        <div className='assignmentReport__questionsSummaryItem'>
                            <p className='assignmentReport__questionSummaryNumber'>
                                {assignmentData.answers.length}
                            </p>
                            <p className='assignmentReport__questionSummaryText'>
                                Total Questions
                            </p>
                        </div>
                        <div className='assignmentReport__questionsSummaryItem'>
                            <p className='assignmentReport__questionSummaryNumber'>
                                {assignmentData.answers.filter(answer => answer.isCorrect).length}
                            </p>
                            <p className='assignmentReport__questionSummaryText'>
                                Correct Answers
                            </p>
                        </div>
                        <div className='assignmentReport__questionsSummaryItem'>
                            <p className='assignmentReport__questionSummaryNumber'>
                                {assignmentData.answers.filter(answer => !answer.isCorrect).length}
                            </p>
                            <p className='assignmentReport__questionSummaryText'>
                                Incorrect Answers
                            </p>
                        </div>
                    </div>
                    <table className='assignmentReport__questionsTable'>
                        <tbody>
                            <tr>
                                <th>Question</th>
                                <th>Correct Answer</th>
                                <th>Your Answer</th>
                                <th>Category</th>
                                {user.customizations.find(customization => customization === 'Show Correct Rate') &&
                                    <th>Correct %</th>
                                }
                                <th>Time Spent</th>
                            </tr>
                            {assignmentData.answers.map((answer, a) => (
                                <tr key={assignmentData.name + answer.questionNumber}>
                                    <td>{a + 1}</td>
                                    <td>{answer.correctChoice !== null
                                        ?   MULTIPLE_CHOICES[answer.correctChoice]
                                        :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                    }</td>
                                    <td>{answer.answer !== null
                                        ?   (answer.correctAnswers.includes(answer.answer)
                                                ? <p>{answer.answer}; Correct</p>
                                                : <p className='assignmentReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                            )
                                        :   answer.choice !== null
                                        ?   (answer.choice === answer.correctChoice
                                                ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                : <p className='assignmentReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                            )
                                        :   <p className='assignmentReport__questionOmitted'>Omitted</p>
                                    }</td>
                                    <td>{answer.category}</td>
                                    {user.customizations.find(customization => customization === 'Show Correct Rate') &&
                                        (answer.correctRate ? <td>{parseFloat(answer.correctRate).toFixed(1)}%</td> : <td></td>)
                                    }
                                    <td>
                                        <p className='assignmentReport__timeSpentGood'>
                                            {formatTime(answer.timeSpent)}
                                        </p>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className='assignmentReport__questionsPrint'>
                <div className='assignmentReport__questionsOverview'>
                    <p className='assignmentReport__moduleName'>
                        {assignmentData.name}
                    </p>
                    <table className='assignmentReport__questionsTable'>
                        <tbody>
                            <tr>
                                <th>Question</th>
                                <th>Correct Answer</th>
                                <th>Your Answer</th>
                                <th>Category</th>
                                {user.customizations.find(customization => customization === 'Show Correct Rate') &&
                                    <th>Correct %</th>
                                }
                                <th>Time Spent</th>
                            </tr>
                            {assignmentData.answers.map((answer, a) => (
                                <tr key={assignmentData.name + answer.questionNumber}>
                                    <td>{a + 1}</td>
                                    <td>{answer.correctChoice !== null
                                        ?   MULTIPLE_CHOICES[answer.correctChoice]
                                        :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                    }</td>
                                    <td>{answer.answer !== null
                                        ?   (answer.correctAnswers.includes(answer.answer)
                                                ? <p>{answer.answer}; Correct</p>
                                                : <p className='assignmentReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                            )
                                        :   answer.choice !== null
                                        ?   (answer.choice === answer.correctChoice
                                                ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                : <p className='assignmentReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                            )
                                        :   <p className='assignmentReport__questionOmitted'>Omitted</p>
                                    }</td>
                                    <td>{answer.category}</td>
                                    {user.customizations.find(customization => customization === 'Show Correct Rate') &&
                                        (answer.correctRate ? <td>{parseFloat(answer.correctRate).toFixed(1)}%</td> : <td></td>)
                                    }
                                    <td>
                                        <p className='assignmentReport__timeSpentGood'>
                                            {formatTime(answer.timeSpent)}
                                        </p>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default AssignmentReport;